import styled from "@emotion/styled"
import { Value } from "@stringtale/react"
import { useSuspenseQuery } from "@tanstack/react-query"
import BulletinBoardCard, {
  getLinkProps as getBulletinLinkProps,
} from "app/bulletinboard/components/BulletinBoardCard"
import BulletinBoardGridBase from "app/bulletinboard/components/BulletinBoardGrid"
import Breadcrumb from "app/core/components/Breadcrumb"
import { ORDER_BY } from "app/core/consts"
import { useCurrentGroup } from "app/core/hooks/useCurrentGroup"
import { useCurrentOrganization } from "app/core/hooks/useCurrentOrganization"
import { getIsExamine } from "app/core/hooks/useIsExamine"
import { getIsReopened } from "app/core/hooks/useIsReopened"
import { getIsSubmitted } from "app/core/hooks/useIsSubmitted"
import { getLink } from "app/core/hooks/useLinks"
import usePopoverSeen from "app/core/hooks/usePopoverSeen"
import DashboardLayout from "app/core/layouts/DashboardLayout"
import ActueelCard from "app/home/components/ActueelCard"
import MostViewedLessons from "app/home/components/MostViewedLessons"
import { HOME_MODAL_KEY } from "app/home/components/HomeModal"
import CategorySlider from "app/leerkracht/components/CategorySlider"
import { trpc } from "app/trpc"
import {
  parseAsBoolean,
  parseAsInteger,
  parseAsString,
  parseAsStringEnum,
  useQueryState,
} from "nuqs"

import { MODAL_QUERY_BULLETIN_BOARD_TYPE_VALUE } from "app/playlists/consts"

import BlueprintModal from "app/schooljaren/components/BlueprintModal"
import { useHasSchoolyears } from "app/schooljaren/hooks/useHasSchoolyears"

import db from "db"
import { SubscriptionType, SupportContentType } from "db/enums"
import { useAtom } from "jotai"
import { GetServerSidePropsContext } from "next"
import Link from "next/link"

import { Suspense, useEffect, useMemo, useState } from "react"

import { css } from "@emotion/react"
import { TbEdit } from "@react-icons/all-files/tb/TbEdit"
import { TbPlus } from "@react-icons/all-files/tb/TbPlus"
import { AnalyticsObject } from "app/analytics/utils/generateAnalyticsObject"
import getAnalyticsContentGroups from "app/analytics/utils/getAnalyticsContentGroups"
import { useCurrentUser } from "app/core/hooks/useCurrentUser"
import HomepageNotificationCenter from "app/home/components/HomepageNotificationCenter"
import DiscoveryButton from "app/support/components/DiscoveryButton"
import { discoveryModalOpenAtom } from "app/support/discoveryModalState"
import dynamic from "next/dynamic"
import {
  HEADER_Z_INDEX,
  LIGHT_GREEN_COLOR,
  PX14,
  PX24,
  PX32,
} from "theme/consts"
import Container from "ui/Container"
import Group from "ui/Group"
import { LoadingOverlay } from "ui/LoadingContainer"
import RoundedButton from "ui/RoundedButton"
import Stack from "ui/Stack"
import TextButton from "ui/TextButton"
import { default as Title } from "ui/Title"
import { createContext, getServerSidePropsAuth } from "app/server/trpc"

const BulletinAddModal = dynamic(
  () => import("app/bulletinboard/components/BulletinAddModal")
)

type Color = "green" | "yellow"

const Root = styled.div`
  container-type: inline-size;
`

const Header = styled.div`
  background-color: ${LIGHT_GREEN_COLOR};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px 0 0px;
  margin-bottom: 40px;
`

const EmptyBulletinBoardItem = styled.div`
  aspect-ratio: 1/1;
  border-radius: 8px;
  box-shadow: 0px 3px 6px #00000029;
`

const AddButton = styled(RoundedButton)`
  aspect-ratio: 1/1;
  border-radius: 100%;
  width: 48px;
  height: 48px;
  box-shadow: 0px 2px 10px #00000033;
`

const BulletinBoardGrid = styled(BulletinBoardGridBase)<{ itemCount: number }>`
  display: grid;
  grid-template-rows: 1fr;
  @container (min-width: 1280px) {
    ${({ itemCount }) => calculateEmptySlotAmount(itemCount, 5)};
  }
  @container (min-width: 1024px) AND (max-width: 1279px) {
    grid-template-columns: repeat(4, 1fr);
    ${({ itemCount }) => calculateEmptySlotAmount(itemCount, 4)}
  }
  @container (min-width: 768px) AND (max-width: 1023px) {
    grid-template-columns: repeat(3, 1fr);
    ${({ itemCount }) => calculateEmptySlotAmount(itemCount, 3)}
  }
  @container (max-width: 767px) {
    grid-template-columns: repeat(2, 1fr);
    ${({ itemCount }) => calculateEmptySlotAmount(itemCount, 2)}
  }
`

const GridWrapper = styled.div`
  position: relative;
  width: 100%;
`

const calculateEmptySlotAmount = (itemCount: number, overflow: number) => {
  if (itemCount > overflow) {
    return css`
      & > :nth-child(n + ${overflow * 2 + 1}) {
        display: none;
      }
    `
  } else {
    return css`
      & > :nth-child(n + ${overflow + 1}) {
        display: none;
      }
    `
  }
}

const EmptyItem = styled.div`
  background-color: #2871631a;
  border-radius: 8px;
  aspect-ratio: 1/1;
  position: relative;
  overflow: clip;
`

const ActueelCards = styled.div`
  width: 100%;
  box-sizing: border-box;
  display: grid;
  gap: 24px;

  grid-template-columns: repeat(3, minmax(0, 1fr));
  justify-items: stretch;

  @media screen and (max-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media screen and (max-width: 767px) {
    grid-template-columns: minmax(0, 1fr);
  }
`

const InnerHomePage = () => {
  const currentUser = useCurrentUser()
  const currentGroup = useCurrentGroup()
  const currentSchool = useCurrentOrganization()
  const [subscriptionType] = useQueryState(
    "subscriptionType",
    parseAsStringEnum<SubscriptionType>(
      Object.values(SubscriptionType) as SubscriptionType[]
    )
  )
  const [groupYear] = useQueryState("groupYear", parseAsInteger)

  // Fetch all queries in parallel using useSuspenseQueries
  const { data: homePage } = useSuspenseQuery({
    ...trpc.home.getHomePageInfo.queryOptions({ subscriptionType, groupYear }),
    staleTime: currentUser?.role === "ADMIN" ? 0 : 600000,
  })

  const { data: bulletinBoardItems } = useSuspenseQuery({
    ...trpc.bulletinboard.getBulletinBoardItems.queryOptions(),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 30000,
  })

  const { data: blueprint } = useSuspenseQuery({
    ...trpc.schooljaren.getBlueprint.queryOptions(),
    refetchOnWindowFocus: false,
  })

  const { state: stateBlueprintModal, toggle: toggleBlueprintModal } =
    usePopoverSeen("SCHOOLYEAR")

  const { state: stateDiscoveryButton, toggle: toggleDiscoveryButton } =
    usePopoverSeen("DISCOVERY")

  const [isAddBulletinItemOpen, setIsAddBulletinItemOpen] = useState(false)

  const [discoveryModalOpen, setDiscoveryModalOpen] = useAtom(
    discoveryModalOpenAtom
  )

  const [_open, setOpen] = useQueryState("bibliotheek", parseAsString)

  const hasSchoolyears = useHasSchoolyears()

  const [hasJustClosedBlueprintModal, setHasJustClosedBlueprintModal] =
    useState(false)

  const analyticsObject = useMemo<Partial<AnalyticsObject>>(() => {
    return homePage
      ? {
          contentType: "homePage",
          contentIsSpecialEducation: homePage.isSpecialEducation,
          ...getAnalyticsContentGroups(homePage.grades),
        }
      : {}
  }, [homePage])

  const {
    state: stateHomeModal,
    toggle: toggleHomeModal,
    suppress: suppressHomeModal,
  } = usePopoverSeen(HOME_MODAL_KEY)

  const { state: stateWelcomeTour } = usePopoverSeen("DISCOVERY_WELCOME_TOUR")

  const [_showWelcomeTour, setShowWelcomeTour] = useQueryState(
    "showWelcomeTour",
    parseAsBoolean
  )

  useEffect(() => {
    // Blueprint modal is open supress any other modals
    if (
      blueprint &&
      hasSchoolyears &&
      stateBlueprintModal !== "CLOSED" &&
      !hasJustClosedBlueprintModal
    ) {
      setShowWelcomeTour(false)
      setDiscoveryModalOpen(false)
      if (stateHomeModal === "OPENED") {
        suppressHomeModal("MINIMIZED")
      }
    }

    // Should open welcome tour, validate if blueprint modal would be open
    // if it would be open, don't open the welcome tour
    if (
      stateWelcomeTour === "OPENED" &&
      !hasJustClosedBlueprintModal &&
      !(blueprint && hasSchoolyears && stateBlueprintModal !== "CLOSED")
    ) {
      setShowWelcomeTour(true)

      if (stateHomeModal === "OPENED") {
        suppressHomeModal("MINIMIZED")
      }
    }
  }, [
    stateWelcomeTour,
    setShowWelcomeTour,
    stateBlueprintModal,
    hasJustClosedBlueprintModal,
    blueprint,
    hasSchoolyears,
    suppressHomeModal,
    setDiscoveryModalOpen,
    stateHomeModal,
  ])

  return (
    <DashboardLayout
      type="HOME"
      analyticsObject={analyticsObject}
      supportContentType={SupportContentType.HOME}
      suppressHomeModal={suppressHomeModal}
      stateHomeModal={stateHomeModal}
    >
      <Root>
        <Stack gap="0px">
          {currentUser?.role !== "ADMIN" && (
            <Breadcrumb
              backgroundColor={LIGHT_GREEN_COLOR}
              items={[
                <Breadcrumb.Item isActive>
                  {currentSchool?.displayTitle}
                </Breadcrumb.Item>,
              ]}
            />
          )}
          <Header>
            <Stack gap="40px" align="stretch">
              <Stack>
                <Container>
                  <Stack>
                    <HomepageNotificationCenter blueprint={blueprint} />
                    <Stack
                      align="flex-start"
                      gap="16px"
                      style={{ paddingBottom: 60 }}
                    >
                      <Group align="center" gap="20px">
                        <Group align="center" gap="16px">
                          <AddButton
                            variant="filled"
                            onClick={() => setIsAddBulletinItemOpen(true)}
                          >
                            <TbPlus size={24} />
                          </AddButton>
                          <Title weight="900" size={PX32}>
                            <Value
                              name="apps.web.pages.index.prikbord_group"
                              format={{
                                groupName: currentGroup?.displayTitle,
                              }}
                            >
                              {`Prikbord {groupName}`}
                            </Value>
                          </Title>
                        </Group>
                        <TextButton
                          size={PX14}
                          onClick={() =>
                            setOpen(MODAL_QUERY_BULLETIN_BOARD_TYPE_VALUE)
                          }
                        >
                          <Value name="apps.web.pages.index.aanpassen">
                            Aanpassen
                          </Value>
                          <TbEdit size={16} strokeWidth={3} />
                        </TextButton>
                      </Group>
                      <GridWrapper>
                        <BulletinBoardGrid
                          itemCount={bulletinBoardItems.length}
                        >
                          {bulletinBoardItems.map((item) => (
                            <BulletinBoardCard
                              key={item.id}
                              item={item}
                              {...getBulletinLinkProps(item)}
                            />
                          ))}
                          {[...Array(10)].map(function (x, i) {
                            return <EmptyItem key={i} />
                          })}
                        </BulletinBoardGrid>
                      </GridWrapper>
                    </Stack>
                  </Stack>
                </Container>
              </Stack>
            </Stack>
          </Header>

          {currentUser?.role === "ADMIN" && (
            <Suspense fallback={null}>
              <MostViewedLessons />
            </Suspense>
          )}

          <Container.Root>
            <Stack gap="60px">
              {homePage && homePage.homeCards?.length > 0 && (
                <Container.Content>
                  <Stack align="flex-start" gap="32px">
                    <Title size={PX24} weight={"900"}>
                      {homePage.displayTitle || (
                        <Value name="apps.web.pages.index.uitgelicht_voor_jouw_groep">
                          Uitgelicht voor jouw groep
                        </Value>
                      )}
                    </Title>

                    <ActueelCards>
                      {homePage.homeCards.map((content) => (
                        <ActueelCard content={content} key={content.id} />
                      ))}
                    </ActueelCards>
                  </Stack>
                </Container.Content>
              )}
              <Container.Content>
                <Stack align="flex-start" gap="32px">
                  <Group gap="16px" align="center">
                    <Title size={PX24} weight={"900"}>
                      <Value name="apps.web.pages.index.categorieen">
                        Categorieën
                      </Value>
                    </Title>
                    <TextButton
                      size={PX14}
                      as={Link}
                      // @ts-ignore
                      href="/categorieen"
                    >
                      <Value name="apps.web.pages.index.bekijk_alles_categorieen">
                        Bekijk alles
                      </Value>
                      &nbsp;&gt;
                    </TextButton>
                  </Group>
                  <CategorySlider />
                </Stack>
              </Container.Content>
            </Stack>
          </Container.Root>
        </Stack>
      </Root>
      {blueprint && hasSchoolyears && stateBlueprintModal !== "CLOSED" ? (
        <BlueprintModal
          blueprint={blueprint}
          setHasJustClosedBlueprintModal={setHasJustClosedBlueprintModal}
          state={stateBlueprintModal}
          toggle={toggleBlueprintModal}
          stateHomeModal={stateHomeModal}
          suppressHomeModal={suppressHomeModal}
        />
      ) : (
        <Group
          gap="16px"
          align="center"
          style={{
            position: "fixed",
            bottom: 20,
            right: 90,
            zIndex: HEADER_Z_INDEX,
          }}
        >
          {
            // Home modal disabled as of 2025-08-01 - https://app.asana.com/1/1147785086346889/inbox/1171671462131767/item/1210934890353356/story/1210935690788572
            // <HomeModal state={stateHomeModal} toggle={toggleHomeModal} />
          }
          {stateDiscoveryButton !== "CLOSED" && (
            <DiscoveryButton
              toggleDiscoveryButton={toggleDiscoveryButton}
              currentUser={currentUser}
            />
          )}
        </Group>
      )}
      <BulletinAddModal
        isBulletinAddOpen={isAddBulletinItemOpen}
        setIsBulletinAddOpen={setIsAddBulletinItemOpen}
      />
    </DashboardLayout>
  )
}

const Home = () => {
  // This makes sure the user is authenticated before rendering the page
  // Allowing for an early redirect to the login page if they're not authenticated
  // This prevents costly queries from being made when the user is not authenticated
  const { data: authenticated } = useSuspenseQuery(
    trpc.users.getAuthenticated.queryOptions(undefined)
  )

  return (
    <Suspense fallback={<LoadingOverlay />}>
      <InnerHomePage />
    </Suspense>
  )
}

export default Home

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
  const reopen = getIsReopened(ctx.query)
  const examine = getIsExamine(ctx.query)
  const submitted = getIsSubmitted(ctx.query)

  const saveId = reopen || examine || submitted

  if (!saveId)
    return {
      props: {},
    }

  /// Redirect root /ingeleverd/:saveId, /nakijken/:saveId etc, to first submittable slide

  const { session } = await getServerSidePropsAuth(ctx.req)
  if (!session) return { notFound: true }

  const savedSubject = await db.savedSubject.findFirst({
    where: {
      id: saveId,
    },
    include: {
      subject: {
        include: {
          headSubject: {
            include: {
              group: {
                include: {
                  lesson: true,
                },
              },
            },
          },
          slides: {
            where: {
              isSubmittable: !!submitted || !!examine ? true : undefined,
            },
            take: 1,
            ...ORDER_BY,
          },
        },
      },
    },
  })

  if (!savedSubject) {
    return { notFound: true }
  }
  const lessonSlug = savedSubject.subject.headSubject.group.lesson.slug
  const headSlug = savedSubject.subject.headSubject.slug
  const subSlug = savedSubject.subject.slug
  const slideSlug = savedSubject.subject.slides[0].slug

  const href = getLink(ctx.query, `/les/${lessonSlug}/${headSlug}`) ///${subSlug}/${slideSlug}
  return {
    redirect: {
      permanent: false,
      destination: href,
    },
  }
}
